import React, { useState } from "react";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { motion } from "framer-motion";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { logAuthEvent } from "../firebase";

const auth = getAuth();

function ResetPassword({ onClose }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resetStage, setResetStage] = useState("form"); // form, sent, error

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);

    try {
      // First check if the user actually exists in our database
      const userQuery = await getDoc(doc(db, "userEmails", email.toLowerCase()));
      
      if (!userQuery.exists()) {
        // User not found, but we'll show a success message anyway for security
        logAuthEvent("password_reset_attempt", { 
          email, 
          success: false, 
          reason: "user_not_found"
        });
        setResetStage("sent");
        setMessage("If an account exists with this email, a password reset link has been sent.");
        return;
      }
      
      // User exists, send the reset email
      await sendPasswordResetEmail(auth, email, {
        // Custom URL here if needed with your domain, Firebase will append action and other parameters
        url: window.location.origin + "/login",
        handleCodeInApp: false,
      });
      
      logAuthEvent("password_reset_requested", { 
        email, 
        success: true 
      });
      
      setResetStage("sent");
      setMessage("✅ Password reset email sent! Check your inbox and follow the instructions.");
      setEmail("");
    } catch (error) {
      console.error("Password reset error:", error);
      logAuthEvent("password_reset_error", { 
        email, 
        errorCode: error.code,
        errorMessage: error.message 
      });
      
      setResetStage("error");
      
      if (error.code === "auth/invalid-email") {
        setError("Invalid email format. Please enter a valid email address.");
      } else if (error.code === "auth/user-not-found") {
        // For security, we don't want to reveal if a user exists or not
        setResetStage("sent");
        setMessage("If an account exists with this email, a password reset link has been sent.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many requests. Please try again later.");
      } else {
        setError(`Unable to send reset email. Please try again later or contact support.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3 } },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={modalVariants}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-2xl font-bold"
          aria-label="Close"
        >
          ×
        </button>

        {resetStage === "form" && (
          <>
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Reset Password</h2>
            <p className="text-gray-600 mb-4">
              Enter your email address below, and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full p-3 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-black placeholder-gray-400"
                required
                disabled={isLoading}
              />
              <button
                type="submit"
                className={`w-full bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition-colors duration-300 font-semibold ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Email"}
              </button>
              {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}
            </form>
          </>
        )}

        {resetStage === "sent" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-gray-800">Check Your Email</h2>
            <p className="text-gray-600 mb-4">
              {message || "We've sent a password reset link to your email."}
            </p>
            <p className="text-gray-500 text-sm mb-6">
              If you don't see the email, check your spam folder or try again.
            </p>
          </div>
        )}

        {resetStage === "error" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2 text-gray-800">Something Went Wrong</h2>
            <p className="text-red-600 mb-4">
              {error || "Failed to send reset email. Please try again."}
            </p>
            <button
              onClick={() => setResetStage("form")}
              className="mt-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300 text-sm"
            >
              Try Again
            </button>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-3 text-purple-600 hover:underline text-sm block mx-auto text-center w-full"
        >
          Back to Login
        </button>
      </motion.div>
    </motion.div>
  );
}

export default ResetPassword;